import { Box, CircularProgress, Typography } from '@mui/material';
import BackendService from 'BackendService';
import React, { Component } from 'react';
import IRRModal from '../../components/admin/FundOpportunityAdmin/IRRModal';
import MessageToast from '../../components/MessageToast';
import { StyledButton } from '../../components/styles/StyledButtons';
import { StyledHeading } from '../../components/styles/styles';
import FundOpportunity from '../../models/FundOpportunity';
import { IRRData } from '../../models/IRRDataPoint';
import { IRRUpdate } from '../../models/IRRUpdate';
import IRRDataTable from './IRRManagement/IRRDataTable';
import IRRPeriodSelector from './IRRManagement/IRRPeriodSelector';

interface IRRManagementPageProps {
  fundOpportunity: FundOpportunity;
}

interface IRRManagementPageState {
  irrUpdates: IRRUpdate[];
  selectedLabel: string;
  irrData?: IRRData;
  loading: boolean;
  dataLoading: boolean;
  irrModalOpen?: boolean;
  irrFile?: File;
  irrMonth?: string;
  isSubmitting: boolean;
  toastMessage: string | null;
  toastType: 'error' | 'warning' | 'info' | 'success' | null;
}

export default class IRRManagementPage extends Component<
  IRRManagementPageProps,
  IRRManagementPageState
> {
  constructor(props: IRRManagementPageProps) {
    super(props);
    this.state = {
      irrUpdates: [],
      selectedLabel: '',
      loading: true,
      dataLoading: false,
      isSubmitting: false,
      toastMessage: null,
      toastType: null,
    };
  }

  componentDidMount() {
    this.loadIRRUpdates();
  }

  loadIRRUpdates = async () => {
    try {
      this.setState({ loading: true });
      const backend = new BackendService();
      const irrUpdates = await backend.getIRRUpdates(this.props.fundOpportunity.id);

      this.setState({
        irrUpdates,
        selectedLabel: irrUpdates.length > 0 ? irrUpdates[irrUpdates.length - 1].label : '',
        loading: false,
      });

      if (irrUpdates.length > 0) {
        const latest = irrUpdates[irrUpdates.length - 1];
        this.loadIRRData(latest.label, latest);
      }
    } catch (error) {
      console.error('Failed to load IRR updates:', error);
      this.setState({ loading: false });
      this.showToast('Failed to load IRR updates', 'error');
    }
  };

  loadIRRData = async (label: string, irrUpdate?: IRRUpdate) => {
    const update = irrUpdate || this.state.irrUpdates.find((u) => u.label === label);
    if (!update) {
      return;
    }

    try {
      this.setState({ dataLoading: true });
      const backend = new BackendService();
      const irrData = await backend.getParsedIRRDataForLabel(
        this.props.fundOpportunity.id,
        update.month,
      );
      this.setState({ irrData, dataLoading: false });
    } catch (error: unknown) {
      console.error('Failed to load IRR data:', error);
      const err = error instanceof Error ? error : new Error(String(error));
      this.setState({ dataLoading: false });
      this.showToast(`Failed to load IRR data: ${err.message}`, 'error');
    }
  };

  handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedLabel = event.target.value;
    const irrUpdate = this.state.irrUpdates.find((u) => u.label === selectedLabel);

    this.setState({ selectedLabel }, () => {
      this.loadIRRData(selectedLabel, irrUpdate);
    });
  };

  showToast = (message: string, type: 'error' | 'warning' | 'info' | 'success') => {
    this.setState({ toastMessage: message, toastType: type });
    setTimeout(() => {
      this.setState({ toastMessage: null, toastType: null });
    }, 5000);
  };

  handleUploadSuccess = (message: string, type: 'error' | 'warning' | 'info' | 'success') => {
    this.showToast(message, type);
    if (type === 'success') {
      this.loadIRRUpdates();
    }
  };

  render() {
    const {
      irrUpdates,
      selectedLabel,
      irrData,
      loading,
      dataLoading,
      irrModalOpen,
      irrFile,
      irrMonth,
      isSubmitting,
      toastMessage,
      toastType,
    } = this.state;

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3 }}>
        <StyledHeading headerVariant="heading2">
          Cashflow Reports Management - {this.props.fundOpportunity.name}
        </StyledHeading>

        <StyledButton
          variant="contained"
          onClick={() => this.setState({ irrModalOpen: true })}
          sx={{ mb: 2 }}
        >
          Upload Cashflow Report
        </StyledButton>

        {irrUpdates.length > 0 && (
          <>
            <IRRPeriodSelector
              irrUpdates={irrUpdates}
              selectedLabel={selectedLabel}
              dataLoading={dataLoading}
              onLabelChange={this.handleMonthChange}
            />
            <IRRDataTable irrData={irrData} dataLoading={dataLoading} />
          </>
        )}

        {irrUpdates.length === 0 && (
          <Typography variant="body1" sx={{ py: 2 }}>
            No cashflow reports available. Upload a cashflow report to get started.
          </Typography>
        )}

        <IRRModal
          open={!!irrModalOpen}
          fundOpportunityId={this.props.fundOpportunity.id}
          irrFile={irrFile}
          irrMonth={irrMonth}
          isSubmitting={isSubmitting}
          onClose={() => this.setState({ irrModalOpen: false })}
          onFileChange={(file) => this.setState({ irrFile: file })}
          onMonthChange={(month) => this.setState({ irrMonth: month })}
          onSubmittingChange={(submitting) => this.setState({ isSubmitting: submitting })}
          onShowToast={this.handleUploadSuccess}
        />

        <MessageToast
          message={toastMessage ?? ''}
          type={toastType ?? 'info'}
          open={!!toastMessage}
          onClose={() => this.setState({ toastMessage: null, toastType: null })}
          autoHideDuration={5000}
        />
      </Box>
    );
  }
}
